import { languageLabels, personDisplayName, speaksLanguage, type LanguageId, type Person, type ReadinessState } from '@vigeo/shared';
import { NO_AREA, inServiceArea, matchesStaffQuery, placesOf, serviceAreaOf } from './staff.js';
import { NO_ROLE_CHOICE, NO_ROLE_LABEL, holdsJobTitle, jobTitleLabel } from './coverageRules.js';

/**
 * The Roster's filters, as ONE value (Roster v2, #478).
 *
 * The filter row, the column-header menus and the tag line all read and write this one object, so they
 * cannot disagree. Nothing here is stored: it is derived on the page from the people already loaded
 * (hard rule 4).
 */

/** "Not tracked yet" is not a readiness state: it is what staff added by hand read (ADR-016 §4). */
export type ComplianceChoice = 'not-tracked' | ReadinessState;
export type StatusChoice = 'active' | 'inactive';
export type PhoneChoice = 'has' | 'none';
export type NameSort = 'asc' | 'desc';

export interface RosterFilters {
  readonly query: string;
  /** Typed service areas, compared without regard to case, and `NO_AREA`. Empty means every area. */
  readonly areas: readonly string[];
  /**
   * Place codes (ADR-021; an old record may still hold a Google place id): chosen in the Place filter, or by a pin or a row of the Coverage card (#473, #563). This is the ONE
   * place selection on the Roster. NOT one of the four parts that
   * are remembered or saved (ADR-019): the server accepts only a typed area in use, so a place id is held
   * for this visit only. Absent means none.
   */
  readonly places?: readonly string[];
  /**
   * Staff with NO place at all (#689: "Not on the map: 2 staff with no place yet. Show them"). It is part of the place selection:
   * with places chosen as well, a person in any of them OR with no place matches. Like `places` it is held for this visit only
   * and is never remembered or saved (ADR-019). Absent means off.
   */
  readonly noPlace?: boolean;
  /**
   * Languages spoken (#772): a person matches if they speak ANY chosen one. Like `places` it is held for this visit only and
   * is never remembered or saved (ADR-019 keeps four parts), so it is not one of them. Absent means every language.
   */
  readonly languages?: readonly LanguageId[];
  /**
   * Job titles (#804): job profile keys, and `NO_ROLE_CHOICE` for staff with none. A person matches if they hold ANY chosen one. The
   * map's counts follow it, so coverage reads per discipline. Held for this visit only, like `languages` (ADR-019 keeps four parts).
   * Absent means every job title.
   */
  readonly jobTitles?: readonly string[];
  readonly compliance: readonly ComplianceChoice[];
  /** Both ticked, or neither, means no filter. */
  readonly statuses: readonly StatusChoice[];
  /** Both ticked, or neither, means no filter. */
  readonly phones: readonly PhoneChoice[];
}

export const noRosterFilters: RosterFilters = { query: '', areas: [], compliance: [], statuses: [], phones: [] };

export const COMPLIANCE_NOT_TRACKED_LABEL = 'Not tracked yet';
export const STATUS_LABEL: Record<StatusChoice, string> = { active: 'Active', inactive: 'Inactive' };
export const PHONE_LABEL: Record<PhoneChoice, string> = { has: 'Has a phone', none: 'No phone' };
/** What the "no place" choice is called in a tag (`NO_PLACE_YET` of the places feature says the same of a person). */
export const NO_PLACE_LABEL = 'No place yet';
/** The tag key of the "no place" choice (`filterTags`, `withoutTag`). */
export const NO_PLACE_TAG = 'noplace';

/** A two-way choice filters only when exactly one side is ticked. */
function narrowed<T>(chosen: readonly T[], all: number): boolean {
  return chosen.length > 0 && chosen.length < all;
}
export const statusFilterOn = (f: RosterFilters) => narrowed(f.statuses, 2);
export const phoneFilterOn = (f: RosterFilters) => narrowed(f.phones, 2);

/** Whether any filter is on (the search box counts). */
export function filtersAreOn(f: RosterFilters): boolean {
  return f.query.trim() !== '' || f.areas.length > 0 || (f.places ?? []).length > 0 || f.noPlace === true || (f.languages ?? []).length > 0 || (f.jobTitles ?? []).length > 0 || f.compliance.length > 0 || statusFilterOn(f) || phoneFilterOn(f);
}

/** How many choices a column's filter holds, for the "Compliance · 2" on its header. */
export const complianceCount = (f: RosterFilters) => f.compliance.length;
export const nameFilterCount = (f: RosterFilters) => (statusFilterOn(f) ? 1 : 0);
export const phoneCount = (f: RosterFilters) => (phoneFilterOn(f) ? 1 : 0);

/** What a tracked person's readiness is right now, or null while it is still being derived or could not be. */
export type ReadinessLookup = (id: string) => ReadinessState | null;

/**
 * A person's compliance as a filterable value. Staff added by hand are `not-tracked` and nothing
 * else: they are never counted under a readiness value (ADR-016 §4). A tracked person whose checklist
 * has not arrived has no value yet and matches no compliance choice.
 */
export function complianceOf(person: Person, readinessOf: ReadinessLookup, tracked: boolean): ComplianceChoice | null {
  return tracked ? readinessOf(person.id) : 'not-tracked';
}

export function matchesRosterFilters(person: Person, f: RosterFilters, readinessOf: ReadinessLookup, tracked: boolean, placeNameOf?: (placeId: string) => string | undefined): boolean {
  if (!matchesStaffQuery(person, f.query, placeNameOf)) return false;
  // Typed areas and places are one menu: a person matches if they are in ANY chosen area or hold ANY chosen place, or, with
  // "no place" chosen, hold no place at all.
  const places = f.places ?? [];
  const noPlace = f.noPlace === true;
  if (f.areas.length > 0 || places.length > 0 || noPlace) {
    const held = placesOf(person);
    const inArea = f.areas.some((area) => inServiceArea(person, area));
    const inPlace = places.some((id) => held.some((place) => place.placeId === id));
    if (!inArea && !inPlace && !(noPlace && held.length === 0)) return false;
  }
  const languages = f.languages ?? [];
  if (languages.length > 0 && !languages.some((id) => speaksLanguage(person.languages, id))) return false;
  const jobTitles = f.jobTitles ?? [];
  if (jobTitles.length > 0 && !holdsJobTitle(person, jobTitles)) return false;
  if (f.compliance.length > 0) {
    const value = complianceOf(person, readinessOf, tracked);
    if (value === null || !f.compliance.includes(value)) return false;
  }
  if (statusFilterOn(f) && !f.statuses.includes(person.lifecycle === 'inactive' ? 'inactive' : 'active')) return false;
  if (phoneFilterOn(f)) {
    const has = hasPhone(person);
    if (!f.phones.includes(has ? 'has' : 'none')) return false;
  }
  return true;
}

export const hasPhone = (person: Person) => (person.contact.phone ?? '').trim() !== '';

/** Name A to Z (the default) or Z to A, by the name as shown. */
export function sortByName(people: readonly Person[], sort: NameSort): Person[] {
  const sign = sort === 'asc' ? 1 : -1;
  return [...people].sort((a, b) => sign * personDisplayName(a.name).localeCompare(personDisplayName(b.name), undefined, { sensitivity: 'base' }));
}

export interface FilterTag {
  /** Which choice this tag stands for, so removing it changes exactly that one. */
  readonly key: string;
  readonly label: string;
}

/** One tag per active choice, in column order: search, typed service area, place, language, job title, compliance, status, phone. `placeLabelOf` says a place's words (the page gives the list's, `placeWords`). */
export function filterTags(f: RosterFilters, complianceLabel: (choice: ComplianceChoice) => string, placeLabelOf: (placeId: string) => string): FilterTag[] {
  const tags: FilterTag[] = [];
  if (f.query.trim() !== '') tags.push({ key: 'query', label: `Search: ${f.query.trim()}` });
  for (const area of f.areas) tags.push({ key: `area:${area}`, label: `Service area: ${area === NO_AREA ? 'No service area' : area}` });
  for (const id of f.places ?? []) tags.push({ key: `place:${id}`, label: `Place: ${placeLabelOf(id)}` });
  if (f.noPlace === true) tags.push({ key: NO_PLACE_TAG, label: `Place: ${NO_PLACE_LABEL}` });
  for (const id of f.languages ?? []) tags.push({ key: `language:${id}`, label: `Language: ${languageLabels[id]}` });
  for (const key of f.jobTitles ?? []) tags.push({ key: `job:${key}`, label: `Job title: ${key === NO_ROLE_CHOICE ? NO_ROLE_LABEL : jobTitleLabel(key)}` });
  for (const choice of f.compliance) tags.push({ key: `compliance:${choice}`, label: `Compliance: ${complianceLabel(choice)}` });
  if (statusFilterOn(f)) for (const choice of f.statuses) tags.push({ key: `status:${choice}`, label: `Status: ${STATUS_LABEL[choice]}` });
  if (phoneFilterOn(f)) for (const choice of f.phones) tags.push({ key: `phone:${choice}`, label: `Phone: ${PHONE_LABEL[choice]}` });
  return tags;
}

/** The filters with the "no place" choice taken off. The field is left out, not set to false, so the value compares equal to one that never had it. */
export function withoutNoPlace(f: RosterFilters): RosterFilters {
  if (f.noPlace === undefined) return f;
  const rest: { -readonly [Part in keyof RosterFilters]: RosterFilters[Part] } = { ...f };
  delete rest.noPlace;
  return rest;
}

/** The filters with one language taken off. Left out when none remain, not set to an empty list, so the value compares equal to one that never had it. */
function withoutLanguage(f: RosterFilters, id: string): RosterFilters {
  const rest = (f.languages ?? []).filter((chosen) => chosen !== id);
  return rest.length > 0 ? { ...f, languages: rest } : withLanguages(f, []);
}

/** The filters with exactly these languages chosen; none leaves the field out, not set to an empty list. */
export function withLanguages(f: RosterFilters, languages: readonly LanguageId[]): RosterFilters {
  if (languages.length > 0) return { ...f, languages };
  if (f.languages === undefined) return f;
  const rest: { -readonly [Part in keyof RosterFilters]: RosterFilters[Part] } = { ...f };
  delete rest.languages;
  return rest;
}

/** The filters with exactly these job titles chosen; none leaves the field out, not set to an empty list. */
export function withJobTitles(f: RosterFilters, jobTitles: readonly string[]): RosterFilters {
  if (jobTitles.length > 0) return { ...f, jobTitles };
  if (f.jobTitles === undefined) return f;
  const rest: { -readonly [Part in keyof RosterFilters]: RosterFilters[Part] } = { ...f };
  delete rest.jobTitles;
  return rest;
}

/** The filters with the choice that `key` names taken off. */
export function withoutTag(f: RosterFilters, key: string): RosterFilters {
  if (key === 'query') return { ...f, query: '' };
  if (key === NO_PLACE_TAG) return withoutNoPlace(f);
  const [kind, ...rest] = key.split(':');
  const value = rest.join(':');
  switch (kind) {
    case 'area': return { ...f, areas: f.areas.filter((a) => a !== value) };
    case 'place': return { ...f, places: (f.places ?? []).filter((id) => id !== value) };
    case 'language': return withoutLanguage(f, value);
    case 'job': return withJobTitles(f, (f.jobTitles ?? []).filter((chosen) => chosen !== value));
    case 'compliance': return { ...f, compliance: f.compliance.filter((c) => c !== value) };
    case 'status': return { ...f, statuses: [] };
    case 'phone': return { ...f, phones: [] };
    default: return f;
  }
}

/** Ticks a value on or off in a list, keeping the order stable. */
export function toggled<T>(list: readonly T[], value: T, on: boolean): T[] {
  const without = list.filter((v) => v !== value);
  return on ? [...without, value] : without;
}

/** Re-exported for the page so it has one import for the service-area helpers it needs. */
export { NO_AREA, serviceAreaOf };

/**
 * A stored phone number for display. Ten digits read as (555) 010-0123; anything else is shown as it
 * was stored, never mangled. Formatting for display does not change the stored value.
 */
export function formatPhone(stored: string | null): string {
  if (stored === null || stored.trim() === '') return '';
  const digits = stored.replace(/[\s().+-]/g, '');
  return /^\d{10}$/.test(digits) ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : stored;
}
